/* eslint-disable import/max-dependencies -- Dialog composes multiple invite components */
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { type UseModalState } from "@src/lib/ui-react";
import { useEffect, useState } from "react";

import { BottomSheet } from "../../../components/BottomSheet";
import { Dialog } from "../../../components/Dialog";
import { DialogHeader } from "../../../components/DialogHeader";
import { Text } from "../../../components/Text";
import { InviteDrawerContent } from "../../../DailyView/components/InviteDrawer/InviteDrawerContent";
import { type Professional } from "../../../DailyView/components/InviteDrawer/types";
import { formatAgentRequestLabel } from "../../../DailyView/utils/formatAgentRequestLabel";
import { useChatInviteShiftDetails } from "./hooks/useChatInviteShiftDetails";
import { useChatWorkerQualification } from "./hooks/useChatWorkerQualification";
import { ShiftDatePickerHeader } from "./ShiftDatePickerHeader";
/* eslint-enable import/max-dependencies */

const DIALOG_TITLE = "Invite to Shift";

interface InviteToShiftDialogProps {
  modalState: UseModalState;
  workplaceId: string;
  timezone: string;
  /** The worker's own qualification, used when the workplace has no matching qualifications. */
  workerQualification?: string;
  /** Qualifications the worker matches at this workplace. */
  matchingFacilityQualifications?: string[];
  /** The qualification from the chat channel's metadata (when opened from chat). */
  channelQualification?: string;
  /**
   * When provided, the invite starts with this professional already selected so
   * the user doesn't have to search for them.
   */
  preselectedProfessional?: Professional;
}

/**
 * Dialog for inviting a worker to a shift.
 * Uses Dialog on desktop and BottomSheet on mobile, following the redesign pattern.
 *
 * Used from:
 * - Chat conversation header (worker pre-selected from the channel)
 * - My Professionals row actions (worker pre-selected from the row)
 *
 * Reuses InviteDrawerContent from the DailyView invite flow for the
 * professional search, validation, and send logic.
 */
export function InviteToShiftDialog(props: InviteToShiftDialogProps) {
  const {
    modalState,
    workplaceId,
    timezone,
    workerQualification,
    matchingFacilityQualifications,
    channelQualification,
    preselectedProfessional,
  } = props;
  const isMobile = useIsMobile();

  const { defaultQualification, availableQualifications } = useChatWorkerQualification({
    channelQualification,
    workerQualification,
    matchingFacilityQualifications,
  });

  const [selectedQualification, setSelectedQualification] = useState<string | undefined>(
    defaultQualification
  );

  useEffect(() => {
    if (isDefined(defaultQualification) && !isDefined(selectedQualification)) {
      setSelectedQualification(defaultQualification);
    }
  }, [defaultQualification, selectedQualification]);

  const activeQualification = selectedQualification ?? defaultQualification;

  const qualificationDisplayName = isDefined(activeQualification)
    ? formatAgentRequestLabel(activeQualification)
    : "";

  const {
    selectedDate,
    selectedTimeSlot,
    shiftDetails,
    isLoading: isLoadingShiftTimes,
    setSelectedDate,
    setSelectedTimeSlot,
  } = useChatInviteShiftDetails({
    workplaceId,
    qualification: activeQualification ?? "",
    qualificationDisplayName,
    timezone,
  });

  if (!activeQualification) {
    const emptyContent = (
      <Box sx={{ padding: 5, textAlign: "center" }}>
        <Text sx={(theme) => ({ color: theme.palette.base?.muted })} variant="body2">
          This professional cannot be invited to shifts at this workplace.
        </Text>
      </Box>
    );

    if (isMobile) {
      return (
        <BottomSheet modalState={modalState}>
          <DialogHeader title={DIALOG_TITLE} onClose={modalState.closeModal} />
          {emptyContent}
        </BottomSheet>
      );
    }

    return (
      <Dialog modalState={modalState} title={DIALOG_TITLE} width={480}>
        {emptyContent}
      </Dialog>
    );
  }

  const content = (
    <InviteToShiftContent
      availableQualifications={availableQualifications}
      isLoadingShiftTimes={isLoadingShiftTimes}
      preselectedProfessional={preselectedProfessional}
      qualification={activeQualification}
      selectedDate={selectedDate}
      selectedTimeSlot={selectedTimeSlot}
      shiftDetails={shiftDetails}
      timezone={timezone}
      workplaceId={workplaceId}
      onClose={modalState.closeModal}
      onDateChange={setSelectedDate}
      onQualificationChange={setSelectedQualification}
      onSuccess={modalState.closeModal}
      onTimeSlotChange={setSelectedTimeSlot}
    />
  );

  if (isMobile) {
    return (
      <BottomSheet maxHeightPercent={95} modalState={modalState}>
        <DialogHeader title={DIALOG_TITLE} onClose={modalState.closeModal} />
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {content}
        </Box>
      </BottomSheet>
    );
  }

  return (
    <Dialog
      disableBodyPadding
      modalState={modalState}
      sx={{
        "& .MuiDialog-paper": {
          maxHeight: "calc(100% - 64px)",
        },
      }}
      title={DIALOG_TITLE}
      width={480}
    >
      {content}
    </Dialog>
  );
}

interface InviteToShiftContentProps {
  availableQualifications: string[];
  isLoadingShiftTimes: boolean;
  preselectedProfessional?: Professional;
  qualification: string;
  selectedDate: Date;
  selectedTimeSlot: import("@src/appV2/redesign/Shift/types").TimeSlot;
  shiftDetails: import("../../../DailyView/components/InviteDrawer/types").ShiftDetails | undefined;
  timezone: string;
  workplaceId: string;
  onClose: () => void;
  onDateChange: (date: Date) => void;
  onQualificationChange: (qualification: string) => void;
  onSuccess: () => void;
  onTimeSlotChange: (slot: import("@src/appV2/redesign/Shift/types").TimeSlot) => void;
}

/**
 * Shared content for both Dialog and BottomSheet.
 * Contains the shift date/slot picker and the invite drawer content.
 */
function InviteToShiftContent(props: InviteToShiftContentProps) {
  const {
    availableQualifications,
    isLoadingShiftTimes,
    preselectedProfessional,
    qualification,
    selectedDate,
    selectedTimeSlot,
    shiftDetails,
    timezone,
    workplaceId,
    onClose,
    onDateChange,
    onQualificationChange,
    onSuccess,
    onTimeSlotChange,
  } = props;

  if (!isDefined(shiftDetails) || isLoadingShiftTimes) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <ShiftDatePickerHeader
          availableQualifications={availableQualifications}
          qualification={qualification}
          selectedDate={selectedDate}
          selectedTimeSlot={selectedTimeSlot}
          timezone={timezone}
          workplaceId={workplaceId}
          onDateChange={onDateChange}
          onQualificationChange={onQualificationChange}
          onTimeSlotChange={onTimeSlotChange}
        />
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 5,
          }}
        >
          <Text sx={(theme) => ({ color: theme.palette.base?.muted })} variant="body2">
            Loading shift times...
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <InviteDrawerContent
      headerContent={
        <ShiftDatePickerHeader
          availableQualifications={availableQualifications}
          qualification={qualification}
          selectedDate={selectedDate}
          selectedTimeSlot={selectedTimeSlot}
          timezone={timezone}
          workplaceId={workplaceId}
          onDateChange={onDateChange}
          onQualificationChange={onQualificationChange}
          onTimeSlotChange={onTimeSlotChange}
        />
      }
      preselectedProfessional={preselectedProfessional}
      shiftDetails={shiftDetails}
      onClose={onClose}
      onSuccess={onSuccess}
    />
  );
}
